/**
 * Page MÉTHODE (#/method) : glossaire de TOUTES les stats proposées — récupérées,
 * calculées, simulées — avec leur définition exacte (formule/seuil), leur source et
 * l'endroit où les trouver. Contenu STATIQUE rendu une fois. Aucune donnée réseau.
 */

import { t } from "../app/i18n.ts";

interface Row {
  readonly s: string; // stat
  readonly d: string; // définition précise
  readonly src: string; // 🛰️ / 🌐 / 🧮 / 📈
  readonly w: string; // où la trouver
}

const sections = (): ReadonlyArray<{ title: string; note?: string; rows: readonly Row[] }> => [
  {
    title: t("method.sec.rawMatch.title"),
    rows: [
      { s: t("method.row.scoreMinuteStatus.s"), d: t("method.row.scoreMinuteStatus.d"), src: "🛰️", w: t("method.row.scoreMinuteStatus.w") },
      { s: t("lf.lineupsTitle"), d: t("method.row.lineups.d"), src: "🛰️", w: t("method.row.lineups.w") },
      { s: t("fiche.eventsMatch"), d: t("method.row.matchEvents.d"), src: "🛰️", w: t("method.row.matchEvents.w") },
    ],
  },
  {
    title: t("method.sec.teamMatch.title"),
    note: t("method.sec.teamMatch.note"),
    rows: [
      { s: t("method.row.possession.s"), d: t("method.row.possession.d"), src: "🛰️", w: t("method.row.possession.w") },
      { s: t("method.row.xg.s"), d: t("method.row.xg.d"), src: "🛰️", w: t("method.row.idem.w") },
      { s: t("method.row.shots.s"), d: t("method.row.shots.d"), src: "🛰️", w: t("method.row.idem.w") },
      { s: t("method.row.cornersFoulsEtc.s"), d: t("method.row.cornersFoulsEtc.d"), src: "🛰️", w: t("method.row.idem.w") },
      { s: t("method.row.passAcc.s"), d: t("method.row.passAcc.d"), src: "🛰️", w: t("method.row.idem.w") },
    ],
  },
  {
    title: t("method.sec.playerMatch.title"),
    rows: [
      { s: t("stats.col.rating"), d: t("method.row.rating.d"), src: "🛰️", w: t("method.row.rating.w") },
      { s: t("method.row.goalsAssists.s"), d: t("method.row.goalsAssists.d"), src: "🛰️", w: t("method.row.idem.w") },
      { s: t("method.row.shotsPassesPct.s"), d: t("method.row.shotsPassesPct.d"), src: "🛰️", w: t("method.row.idem.w") },
      { s: t("method.row.tacklesDuels.s"), d: t("method.row.tacklesDuels.d"), src: "🛰️", w: t("method.row.idem.w") },
    ],
  },
  {
    title: t("method.sec.context.title"),
    rows: [
      { s: t("method.row.elo.s"), d: t("method.row.elo.d"), src: "🌐 eloratings", w: t("method.row.elo.w") },
      { s: t("fiche.squadValue.subtitle"), d: t("method.row.squadValue.d"), src: "🌐 Transfermarkt", w: t("method.row.squadValue.w") },
      { s: t("method.row.fifaRank.s"), d: t("method.row.fifaRank.d"), src: "🌐 FIFA", w: t("method.row.fifaRank.w") },
      { s: t("method.row.injuries.s"), d: t("method.row.injuries.d"), src: "🛰️", w: t("method.row.injuries.w") },
    ],
  },
  {
    title: t("method.sec.calcMatch.title"),
    rows: [
      { s: t("fiche.dominance"), d: t("method.row.domination.d"), src: "🧮", w: t("method.row.domination.w") },
      { s: t("method.row.deservedResult.s"), d: t("method.row.deservedResult.d"), src: "🧮", w: t("method.row.domination.w") },
      { s: t("method.row.h2h.s"), d: t("method.row.h2h.d"), src: "🛰️ 🧮", w: t("method.row.domination.w") },
      { s: t("method.row.prediction.s"), d: t("method.row.prediction.d"), src: "📈", w: t("method.row.prediction.w") },
    ],
  },
  {
    title: t("method.sec.calcPlayer.title"),
    note: t("method.sec.calcPlayer.note"),
    rows: [
      { s: t("stats.metric.conversion"), d: t("method.row.conversion.d"), src: "🧮", w: t("method.row.conversion.w") },
      { s: t("method.row.per90.s"), d: t("method.row.per90.d"), src: "🧮", w: t("method.row.idem.w") },
      { s: t("method.row.ratesPct.s"), d: t("method.row.ratesPct.d"), src: "🧮", w: t("method.row.idem.w") },
      { s: t("fiche.player.metrics.vsPosAvg"), d: t("method.row.vsPositionAvg.d"), src: "🧮", w: t("method.row.vsPositionAvg.w") },
    ],
  },
  {
    title: t("method.sec.calcTeam.title"),
    rows: [
      { s: t("method.row.teamRating.s"), d: t("method.row.teamRating.d"), src: "🧮", w: t("method.row.teamRating.w") },
      { s: t("fiche.country.attack.title"), d: t("method.row.offEfficiency.d"), src: "🧮", w: t("method.row.domination.w") },
      { s: t("fiche.country.defense.title"), d: t("method.row.defSolidity.d"), src: "🧮", w: t("method.row.domination.w") },
      { s: t("fiche.country.xgDiff.title"), d: t("method.row.xgDiff.d"), src: "🧮", w: t("method.row.domination.w") },
      { s: t("method.row.yield.s"), d: t("method.row.yield.d"), src: "🧮", w: t("method.row.domination.w") },
    ],
  },
  {
    title: t("method.sec.standings.title"),
    rows: [
      { s: t("method.row.groupStandings.s"), d: t("method.row.groupStandings.d"), src: "🧮", w: t("method.row.groupStandings.w") },
      { s: t("method.row.bestThirds.s"), d: t("method.row.bestThirds.d"), src: "🧮", w: t("method.row.bestThirds.w") },
      { s: t("method.row.scorersAssists.s"), d: t("method.row.scorersAssists.d"), src: "🧮", w: t("method.row.scorersAssists.w") },
      { s: t("method.row.bestXiClubs.s"), d: t("method.row.bestXiClubs.d"), src: "🧮", w: t("method.row.bestXiClubs.w") },
      { s: t("teff.section.title"), d: t("method.row.teamEfficiency.d"), src: "🧮", w: t("method.row.bestThirds.w") },
    ],
  },
  {
    title: t("method.sec.modelProjection.title"),
    rows: [
      { s: t("method.row.probabilities.s"), d: t("method.row.probabilities.d"), src: "📈", w: t("method.row.probabilities.w") },
      { s: t("method.row.possibleOpponents.s"), d: t("method.row.possibleOpponents.d"), src: "📈", w: t("method.row.possibleOpponents.w") },
      { s: t("method.row.qualifConditions.s"), d: t("method.row.qualifConditions.d"), src: "🧮", w: t("method.row.qualifConditions.w") },
      { s: t("method.row.citiesByPlace.s"), d: t("method.row.citiesByPlace.d"), src: "📈 🧮", w: t("method.row.citiesByPlace.w") },
    ],
  },
];

const tableHtml = (rows: readonly Row[]): string =>
  `<table class="mth-table"><thead><tr><th>${t("method.table.head.stat")}</th><th>${t("method.table.head.definition")}</th><th>${t("method.table.head.source")}</th><th>${t("method.table.head.where")}</th></tr></thead><tbody>${rows
    .map((r) => `<tr><td class="mth-s">${r.s}</td><td>${r.d}</td><td class="mth-src">${r.src}</td><td class="mth-w">${r.w}</td></tr>`)
    .join("")}</tbody></table>`;

export function renderMethodology(el: HTMLElement): void {
  el.innerHTML = `
    <div class="mth">
      <h1 class="mth-title">${t("method.title")}</h1>
      <p class="mth-intro">${t("method.intro")}</p>
      <ul class="mth-legend">
        <li>🛰️ ${t("method.legend.apiFootball")}</li>
        <li>🌐 ${t("method.legend.otherFeeds")}</li>
        <li>🧮 ${t("method.legend.appCalc")}</li>
        <li>📈 ${t("method.legend.simulation")}</li>
      </ul>
      ${sections().map(
        (sec) =>
          `<section class="mth-sec"><h2 class="mth-h">${sec.title}</h2>${sec.note ? `<p class="mth-note note">${sec.note}</p>` : ""}${tableHtml(sec.rows)}</section>`,
      ).join("")}
      <p class="mth-foot note">${t("method.footer")}</p>
    </div>`;
}
